import { ActionFunction } from 'react-router-dom';
import { client } from '~/api/client';
import {
  parseExcelSpreadsheetTemplateTabsFromFormData,
  parseOptionalTrimmedFormField,
  parseVisibilityFormField
} from '~/api/data/form-data';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import {
  CreateExcelSpreadsheetTemplateDocument,
  CreateExcelSpreadsheetTemplateMutation,
  CreateExcelSpreadsheetTemplateMutationVariables,
  UpdateExcelSpreadsheetTemplateDocument,
  UpdateExcelSpreadsheetTemplateMutation,
  UpdateExcelSpreadsheetTemplateMutationVariables,
  UpdateExcelSpreadsheetTemplatesDocument,
  UpdateExcelSpreadsheetTemplatesMutation,
  UpdateExcelSpreadsheetTemplatesMutationVariables,
  type VisibilityConfigInput
} from '~/api/graphql';

export const createExcelSpreadsheetTemplate = (
  variables: CreateExcelSpreadsheetTemplateMutationVariables
) =>
  mutateData<
    CreateExcelSpreadsheetTemplateMutation,
    CreateExcelSpreadsheetTemplateMutationVariables
  >(client, {
    mutation: CreateExcelSpreadsheetTemplateDocument,
    variables
  });

export const updateExcelSpreadsheetTemplate = (
  variables: UpdateExcelSpreadsheetTemplateMutationVariables
) =>
  mutateData<
    UpdateExcelSpreadsheetTemplateMutation,
    UpdateExcelSpreadsheetTemplateMutationVariables
  >(client, {
    mutation: UpdateExcelSpreadsheetTemplateDocument,
    variables
  });

export const archiveExcelSpreadsheetTemplates = (
  variables: UpdateExcelSpreadsheetTemplatesMutationVariables
) =>
  mutateData<
    UpdateExcelSpreadsheetTemplatesMutation,
    UpdateExcelSpreadsheetTemplatesMutationVariables
  >(client, {
    mutation: UpdateExcelSpreadsheetTemplatesDocument,
    variables
  });

export const updateExcelSpreadsheetTemplatesVisibility = async ({
  templateIds,
  visibility
}: {
  templateIds: string[];
  visibility: VisibilityConfigInput;
}) => {
  try {
    await client.mutate<
      UpdateExcelSpreadsheetTemplatesMutation,
      UpdateExcelSpreadsheetTemplatesMutationVariables
    >({
      mutation: UpdateExcelSpreadsheetTemplatesDocument,
      variables: { templateIds, input: { visibility } }
    });
    return { failedIds: [] as string[] };
  } catch {
    return { failedIds: templateIds };
  }
};

export const submitArchiveExcelSpreadsheetTemplates: ActionFunction = async ({ request }) =>
  submitMutation(
    archiveExcelSpreadsheetTemplates({
      templateIds: (await request.json()).templateIds,
      input: { archive: true }
    })
  );

export const submitUpdateExcelSpreadsheetTemplatesVisibility: ActionFunction = async ({
  request
}) => updateExcelSpreadsheetTemplatesVisibility(await request.json());

export const submitCreateExcelSpreadsheetTemplate: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  const upload = formData.get('uploadTemplate');
  return submitMutation(
    createExcelSpreadsheetTemplate({
      input: {
        title: formData.get('title') as string,
        description: formData.get('description') as string,
        fillGuidance: parseOptionalTrimmedFormField(formData.get('fillGuidance')),
        uploadTemplate: upload instanceof File ? upload : null,
        visibility: parseVisibilityFormField(formData.get('visibility'))
      }
    }),
    async (data) => ({ data: data ?? null })
  );
};

export const submitUpdateExcelSpreadsheetTemplate: ActionFunction = async ({ params, request }) => {
  const formData = await request.formData();
  const upload = formData.get('uploadTemplate');
  const templateFileId = formData.get('templateFileId');
  const fillRaw = formData.get('fillGuidance');
  return submitMutation(
    updateExcelSpreadsheetTemplate({
      id: params.id || '',
      input: {
        title: formData.get('title') as string,
        description: formData.get('description') as string,
        fillGuidance: typeof fillRaw === 'string' ? fillRaw : '',
        // The editor sends exactly one of these: a replacement workbook, or the
        // stored file's ID to keep it.
        uploadTemplate: upload instanceof File ? upload : null,
        templateFileId: typeof templateFileId === 'string' ? templateFileId : null,
        tabs: parseExcelSpreadsheetTemplateTabsFromFormData(formData),
        visibility: parseVisibilityFormField(formData.get('visibility'))
      }
    })
  );
};
